import { useSelectedApPayee } from '@/components/ApPayee/useChangeApPayee';
import { useGetExcisesAllSuspense } from '@/context/services/excise/Excise.service';
import { isForeignCurrencyDisable } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Columns/utils/getForeignCurrencyDisableProps';
import { FormController } from '@moneyforward/ap-frontend-components';
import { Excises } from 'ap-openapi';
import { memo, useMemo } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';
import {
  FormInputNumberCell,
  Props as FormInputNumberCellProps,
} from './FormInputNumberCell';

export type Props<TFieldValues extends FieldValues> =
  FormInputNumberCellProps<TFieldValues> & {
    isForeignCurrencyFlag: boolean;
    currency: string | undefined;
    drExciseId: string | undefined;
  };

const InnerExciseValueCell = <TFieldValues extends FieldValues>({
  control,
  name,
  index,
  isForeignCurrencyFlag,
  currency,
  drExciseId,
  ...rest
}: Props<TFieldValues>) => {
  const selectedPayee = useSelectedApPayee();
  const foreignCurrencyDisabled = isForeignCurrencyDisable({
    isForeignCurrencyFlag,
    paymentMethod: selectedPayee?.payment_method,
    currency: currency,
  });
  const drExciseList = useGetExcisesAllSuspense();
  const canEditExcise = canEditExciseValue(drExciseId, drExciseList);
  const disabled = !canEditExcise || foreignCurrencyDisabled;
  const memorizedInputProps = useMemo(() => ({ disabled }), [disabled]);
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const formName = (
    disabled ? `${name}_disabled` : name
  ) as FieldPath<TFieldValues>;
  return (
    <>
      <FormInputNumberCell<TFieldValues>
        control={control}
        name={formName}
        index={index}
        {...rest}
        inputProps={memorizedInputProps}
      />
      {disabled && (
        <FormController control={control} name={name}>
          {(...rest) => <input {...rest} value={0} hidden />}
        </FormController>
      )}
    </>
  );
};
InnerExciseValueCell.displayName = 'FormInputNumberCell';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const ExciseValueCell = memo(
  InnerExciseValueCell
) as typeof InnerExciseValueCell;
ExciseValueCell.displayName = 'ExciseValueCell';

export const canEditExciseValue = (
  drExciseId: string | undefined,
  drExciseList: Excises[]
): boolean => {
  if (!drExciseId) {
    return true;
  }

  const excise = drExciseList.find((item) => item.id === drExciseId);
  if (!excise) {
    return false;
  }
  // Targetの他にも輸入関連のImportなどあるが、法律的に消費税が必要なのがTargetだけ
  return excise.excise_label_target_type === 'Target';
};
